/**
 * Forma de un fallo reportado por el navegador y su normalización.
 *
 * Vive aparte de `logger.ts` y de `log-settings.ts` porque lo comparten los dos
 * lados: el navegador lo usa para filtrar y recortar antes de enviar, y la ruta
 * para no fiarse de lo que llegó. Por eso **no importa nada del servidor** —
 * `log-settings.ts` arrastraría `pg` al paquete del cliente—, y por eso los
 * cuatro niveles se repiten aquí; son los mismos de `LOG_LEVELS`.
 *
 * Regla de contenido: de la página solo salen mensaje, pila y ruta. Nunca el
 * contenido de un formulario ni el de un daily. Si alguna vez hace falta más
 * contexto, va como campos con nombre —que el logger redacta por nombre—, no
 * como un volcado del estado.
 */

export const CLIENT_LOG_LEVELS = ['error', 'warn', 'info', 'debug'] as const;
export type ClientLogLevel = typeof CLIENT_LOG_LEVELS[number];

const ORDEN: Record<ClientLogLevel, number> = { error: 0, warn: 1, info: 2, debug: 3 };

/** Un evento pesa poco a propósito: una pila entera no aporta y sí satura. */
export const MAX_MSG = 300;
export const MAX_STACK = 1500;
export const MAX_EVENTS_POR_ENVIO = 10;

export interface ClientLogEvent {
  level: ClientLogLevel;
  /** Qué lo produjo: `error`, `unhandledrejection`, `boundary`, `manual`. */
  kind: string;
  msg: string;
  stack?: string;
  /** Ruta de la página, sin query: los identificadores no viajan en la URL. */
  path?: string;
  /** `x-request-id` de la petición que falló, si la hubo: empata con el servidor. */
  srcReq?: string;
  /** `digest` de Next cuando el fallo ocurrió al renderizar en el servidor. */
  digest?: string;
}

export function isClientLogLevel(v: unknown): v is ClientLogLevel {
  return typeof v === 'string' && (CLIENT_LOG_LEVELS as readonly string[]).includes(v);
}

export function levelAllowed(level: ClientLogLevel, minimo: ClientLogLevel): boolean {
  return ORDEN[level] <= ORDEN[minimo];
}

function texto(v: unknown, max: number): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s) return undefined;
  return s.length > max ? `${s.slice(0, max)}…(+${s.length - max})` : s;
}

/** Ruta sin query ni fragmento: lo que identifica la pantalla, nada más. */
function rutaLimpia(v: unknown): string | undefined {
  const s = texto(v, 200);
  return s ? s.split(/[?#]/)[0] : undefined;
}

/**
 * Deja pasar solo eventos bien formados y dentro del nivel. Lo que llega mal se
 * descarta en silencio: es una entrada que cualquiera puede fabricar, así que no
 * merece un error detallado que sirva para tantear la validación.
 */
export function normalizeClientEvents(raw: unknown, minimo: ClientLogLevel): ClientLogEvent[] {
  const lista = Array.isArray(raw) ? raw : [];
  const salida: ClientLogEvent[] = [];
  for (const item of lista.slice(0, MAX_EVENTS_POR_ENVIO)) {
    if (!item || typeof item !== 'object') continue;
    const e = item as Record<string, unknown>;
    if (!isClientLogLevel(e.level) || !levelAllowed(e.level, minimo)) continue;
    const msg = texto(e.msg, MAX_MSG);
    if (!msg) continue;
    salida.push({
      level: e.level,
      kind: texto(e.kind, 40) ?? 'manual',
      msg,
      stack: texto(e.stack, MAX_STACK),
      path: rutaLimpia(e.path),
      srcReq: texto(e.srcReq, 64),
      digest: texto(e.digest, 64),
    });
  }
  return salida;
}

/** Huella para no repetir el mismo fallo una y otra vez dentro de una ventana. */
export function eventFingerprint(e: { level: string; msg: string; stack?: string }): string {
  return `${e.level}|${e.msg}|${(e.stack ?? '').split('\n')[0]}`;
}
